(() => {
  const STOP_WORDS = new Set([
    'a', 'an', 'and', 'are', 'can', 'do', 'does', 'for', 'how', 'i', 'in', 'is', 'it', 'my',
    'of', 'on', 'or', 'the', 'to', 'what', 'with', 'you', 'your',
  ]);
  const LIMITS = { products: 4, recipes: 4, faqs: 3, pages: 3 };
  const RECIPE_LIMIT_SCOPED = 8;

  const normalize = (text) =>
    (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[‘’]/g, "'")
      .replace(/\s+/g, ' ')
      .trim();

  const tokenize = (query) =>
    normalize(query)
      .split(/[^a-z0-9']+/)
      .filter((word) => word.length > 1 && !STOP_WORDS.has(word));

  const escapeHtml = (text) =>
    String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const faqCache = new Map();
  const loadFaqs = (faqUrl) => {
    if (!faqCache.has(faqUrl)) {
      faqCache.set(
        faqUrl,
        fetch(faqUrl, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return [...doc.querySelectorAll('.accordion details[id]')]
              .map((details) => ({
                id: details.id,
                question: details.querySelector('.accordion__title')?.textContent.replace(/\s+/g, ' ').trim() ?? '',
                answer: details.querySelector('.accordion__content')?.textContent.replace(/\s+/g, ' ').trim() ?? '',
              }))
              .filter((faq) => faq.question)
              .map((faq) => ({ ...faq, url: `${faqUrl}#${encodeURIComponent(faq.id)}` }));
          })
          .catch(() => [])
      );
    }
    return faqCache.get(faqUrl);
  };

  const matchFaqs = ({ faqs, query, limit }) => {
    const tokens = tokenize(query);
    if (!tokens.length) return [];
    const patterns = tokens.map((token) => new RegExp(`\\b${escapeRegExp(token)}`));
    return faqs
      .map((faq) => {
        const question = normalize(faq.question);
        const answer = normalize(faq.answer);
        const scores = patterns.map((pattern) => (pattern.test(question) ? 3 : pattern.test(answer) ? 1 : 0));
        const phraseBonus = tokens.length > 1 && question.includes(tokens.join(' ')) ? 4 : 0;
        return { faq, matchesAll: scores.every((score) => score > 0), score: scores.reduce((a, b) => a + b, 0) + phraseBonus };
      })
      .filter((entry) => entry.matchesAll)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map((entry) => entry.faq);
  };

  const excerpt = ({ text, query, length = 110 }) => {
    const clean = (text || '').replace(/\s+/g, ' ').trim();
    if (clean.length <= length) return clean;
    const first = tokenize(query)[0];
    const at = first ? normalize(clean).indexOf(first) : -1;
    const start = at > 40 ? at - 30 : 0;
    const slice = clean.slice(start, start + length).trim();
    return `${start > 0 ? '…' : ''}${slice}…`;
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', width);
      return parsed.toString();
    } catch {
      return url;
    }
  };

  const formatPrice = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) return '';
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  };

  const cleanUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname;
    } catch {
      return url;
    }
  };

  const renderItem = ({ url, title, meta, image, kind }) => `
    <li class="sob-search__item sob-search__item--${kind}">
      <a class="sob-search__link" href="${escapeHtml(url)}">
        ${image ? `<span class="sob-search__thumb"><img src="${escapeHtml(image)}" alt="" width="64" height="64"></span>` : ''}
        <span class="sob-search__text">
          <span class="sob-search__title">${escapeHtml(title)}</span>
          ${meta ? `<span class="sob-search__meta">${escapeHtml(meta)}</span>` : ''}
        </span>
      </a>
    </li>`;

  const renderGroup = ({ title, kind, items }) =>
    items.length
      ? `<div class="sob-search__group sob-search__group--${kind}">
          <p class="sob-search__group-title">${escapeHtml(title)}</p>
          <ul class="sob-search__list" role="list">${items.map((item) => renderItem({ ...item, kind })).join('')}</ul>
        </div>`
      : '';

  const faqItems = ({ faqs, query }) =>
    faqs.map((faq) => ({ url: faq.url, title: faq.question, meta: excerpt({ text: faq.answer, query }) }));

  class SobSearch extends HTMLElement {
    connectedCallback() {
      if (this.input) return;
      this.input = this.querySelector('.sob-search__input');
      this.results = this.querySelector('.sob-search__results');
      this.clearButton = this.querySelector('.sob-search__clear');
      this.scope = this.dataset.scope || 'all';
      this.root = this.dataset.root || '/';
      this.faqUrl = this.dataset.faqUrl || '/pages/faq';
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.requestId = 0;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.scope === 'all' && loadFaqs(this.faqUrl), { once: true });
      this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      this.results.addEventListener('keydown', (event) => this.onResultsKeydown(event));
      this.clearButton.addEventListener('click', () => this.clear());
    }

    clear() {
      this.input.value = '';
      this.onInput();
      this.input.focus();
    }

    onInput() {
      const query = this.input.value.trim();
      this.clearButton.hidden = !query;
      clearTimeout(this.timer);
      if (!query) {
        this.requestId += 1;
        this.setActive(false);
        return;
      }
      this.timer = setTimeout(() => this.search(query), 180);
    }

    setActive(active) {
      this.results.hidden = !active;
      if (!active) this.results.innerHTML = '';
      this.toggleAttribute('data-active', active);
      this.dispatchEvent(new CustomEvent('sob-search:active', { bubbles: true, detail: { active } }));
    }

    async fetchSuggestions(query) {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': this.scope === 'recipes' ? 'article' : 'product,article,page',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag,body',
      });
      const response = await fetch(`${this.root.replace(/\/$/, '')}/search/suggest.json?${params}`);
      if (!response.ok) return { products: [], articles: [], pages: [] };
      const data = await response.json();
      const results = data?.resources?.results ?? {};
      return { products: results.products ?? [], articles: results.articles ?? [], pages: results.pages ?? [] };
    }

    async search(query) {
      const requestId = ++this.requestId;
      const [suggestions, faqs] = await Promise.all([
        this.fetchSuggestions(query).catch(() => ({ products: [], articles: [], pages: [] })),
        this.scope === 'all' ? loadFaqs(this.faqUrl) : Promise.resolve([]),
      ]);
      if (requestId !== this.requestId) return;
      this.render({ query, suggestions, faqs: matchFaqs({ faqs, query, limit: LIMITS.faqs }) });
    }

    render({ query, suggestions, faqs }) {
      const recipes = suggestions.articles
        .filter((article) => cleanUrl(article.url).includes('/blogs/recipes/'))
        .slice(0, this.scope === 'recipes' ? RECIPE_LIMIT_SCOPED : LIMITS.recipes)
        .map((article) => ({
          url: cleanUrl(article.url),
          title: article.title,
          image: sizedImage(article.featured_image?.url || article.image, 128),
        }));

      const products = suggestions.products.slice(0, LIMITS.products).map((product) => ({
        url: cleanUrl(product.url),
        title: product.title,
        meta: product.available === false ? 'Sold out' : `${product.price_min !== product.price_max ? 'From ' : ''}${formatPrice(product.price_min ?? product.price)}`,
        image: sizedImage(product.featured_image?.url || product.image, 128),
      }));

      const pages = suggestions.pages.slice(0, LIMITS.pages).map((page) => ({ url: cleanUrl(page.url), title: page.title }));

      const groups =
        this.scope === 'recipes'
          ? [{ title: 'Recipes', kind: 'recipes', items: recipes }]
          : [
              { title: 'Shop', kind: 'products', items: products },
              { title: 'Recipes', kind: 'recipes', items: recipes },
              { title: 'FAQs', kind: 'faqs', items: faqItems({ faqs, query }) },
              { title: 'Pages', kind: 'pages', items: pages },
            ];

      const body = groups.map(renderGroup).join('');
      const params = new URLSearchParams({ q: query, 'options[prefix]': 'last' });
      if (this.scope === 'recipes') params.set('type', 'article');
      const allUrl = `${this.searchUrl}?${params}`;

      this.results.innerHTML = body
        ? `<div class="sob-search__groups">${body}</div>
           <a class="sob-search__all" href="${escapeHtml(allUrl)}">See all results for “${escapeHtml(query)}” <span aria-hidden="true">→</span></a>`
        : `<p class="sob-search__empty">No ${this.scope === 'recipes' ? 'recipes' : 'matches'} for “${escapeHtml(query)}”. Try ${this.scope === 'recipes' ? '“pasta”, “eggs” or “truffle”' : '“truffle”, “recipes” or “shipping”'}.</p>`;
      this.setActive(true);
    }

    links() {
      return [...this.results.querySelectorAll('a')];
    }

    onInputKeydown(event) {
      if (event.key === 'ArrowDown' && !this.results.hidden) {
        event.preventDefault();
        this.links()[0]?.focus();
      }
    }

    onResultsKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const links = this.links();
      const index = links.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      if (next < 0) this.input.focus();
      else links[Math.min(next, links.length - 1)]?.focus();
    }
  }

  class SobFaqResults extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = "true";
      const query = this.dataset.terms || '';
      const faqs = matchFaqs({ faqs: await loadFaqs(this.dataset.faqUrl || '/pages/faq'), query, limit: 5 });
      if (!faqs.length) return;
      this.innerHTML = `
        <p class="sob-search__group-title">From the FAQ</p>
        <ul class="sob-search__list" role="list">${faqItems({ faqs, query })
          .map((item) => renderItem({ ...item, kind: 'faqs' }))
          .join('')}</ul>`;
      this.hidden = false;
    }
  }

  if (!customElements.get('sob-search')) customElements.define('sob-search', SobSearch);
  if (!customElements.get('sob-faq-results')) customElements.define('sob-faq-results', SobFaqResults);

  const initOverlay = () => {
    const overlay = document.getElementById('sob-search-overlay');
    if (!overlay) return;
    document.body.appendChild(overlay);
    const toggles = [...document.querySelectorAll('.sob-search-toggle')];
    const input = overlay.querySelector('.sob-search__input');
    let lastFocus = null;

    const setOpen = (open) => {
      overlay.hidden = !open;
      document.body.classList.toggle('sob-search-open', open);
      toggles.forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
      if (open) {
        lastFocus = document.activeElement;
        requestAnimationFrame(() => input.focus());
      } else if (lastFocus) {
        lastFocus.focus();
      }
    };

    toggles.forEach((toggle) => toggle.addEventListener('click', () => setOpen(overlay.hidden)));
    overlay.querySelectorAll('[data-sob-search-close]').forEach((el) => el.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !overlay.hidden) setOpen(false);
    });
    window.addEventListener('pageshow', (event) => event.persisted && setOpen(false));
  };

  const initMenuSearch = () => {
    const menu = document.getElementById('mobile-fullscreen-menu');
    if (!menu) return;
    menu.addEventListener('sob-search:active', (event) => menu.classList.toggle('is-searching', event.detail.active));
  };

  const openFaqFromHash = () => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id.startsWith('Details-')) return;
    const details = document.getElementById(id);
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = true;
    details.classList.add('sob-faq-highlight');
    requestAnimationFrame(() => details.scrollIntoView({ block: 'center' }));
  };

  const init = () => {
    initOverlay();
    initMenuSearch();
    openFaqFromHash();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('hashchange', openFaqFromHash);
})();
